import { PortalModuleVariant } from './portal-module-variant';
import {
  BbColorInput,
  ColorType,
  InvariantInput,
  InvariantTranslationKey,
  InvariantTranslationKeyStringInputMap,
  InvariantTranslationKeyStringInputPair,
  ModuleBlueprintInput,
  VersionedStatusInput,
  VersionedStatusTypes,
  XAttachmentPointInput,
  YAttachmentPointInput,
} from '@src/app/generated/globalTypes';
import {
  GetAllModuleBlueprints_allBlueprints,
  GetAllModuleBlueprints_allBlueprints_xAttachmentPoints,
  GetAllModuleBlueprints_allBlueprints_yAttachmentPoints,
} from '@src/app/generated/GetAllModuleBlueprints';
import { ToJsonInputTranslatable } from '@src/app/model/to-json-input-translatable';
import { PortalVector3 } from '@src/app/model/portalVector3';
import {
  Asset,
  BoundingBox,
  BoundingBoxSize,
  draftStatus,
  Events,
  EventTemplate,
  id,
  id as _id,
  Id,
  InvariantTemplate,
  ModuleBlueprint,
  ModuleCategory,
  ModuleType,
  vectorFromRaw,
  Versioned,
  VersionedStatus,
  VisualXAttachmentPoint,
  XAttachmentPoint,
  YAttachmentPoint,
} from '@ess/jg-rule-executor';
import { VisualYAttachmentPoint } from '@ess/jg-rule-executor/dist/src/domain/visual-y-attachment-point';
import { PortalInvariant } from '@src/app/model/portal-invariant';
import { PortalAsset } from '@src/app/model/portal-asset';
import { GetBlueprintsByName_blueprintsByName } from '@src/app/generated/GetBlueprintsByName';
import { BbColor } from '@ess/jg-rule-executor/dist/src/domain/BbColor';

export class PortalModuleBlueprint extends ModuleBlueprint implements ToJsonInputTranslatable, Versioned {
  portalVariants: PortalModuleVariant[];
  declare invariants: PortalInvariant[];

  constructor(
    id?: Id<ModuleBlueprint>,
    name?: string,
    status?: VersionedStatus,
    xAttachmentPoints?: XAttachmentPoint[],
    yAttachmentPoints?: YAttachmentPoint[],
    model?: Asset,
    invariants?: PortalInvariant[],
    events?: Events,
    eventTemplate?: Id<EventTemplate>,
    invariantTemplates?: Id<InvariantTemplate>[],
    visualXAttachmentPoints?: VisualXAttachmentPoint[],
    visualYAttachmentPoint?: VisualYAttachmentPoint,
    groupId?: string,
    boundingBoxes?: BoundingBox[],
    categoryId?: Id<ModuleCategory>,
    categoryReference?: string,
    moduleType?: ModuleType,
    considerAsFree?: boolean,
    orderWeight?: number
  ) {
    super(
      id,
      name,
      status,
      xAttachmentPoints,
      yAttachmentPoints,
      model,
      invariants,
      events,
      eventTemplate,
      invariantTemplates,
      visualXAttachmentPoints,
      visualYAttachmentPoint,
      boundingBoxes,
      categoryId,
      categoryReference,
      moduleType,
      groupId,
      considerAsFree,
      orderWeight
    );
  }

  static fromGql(
    gql: GetAllModuleBlueprints_allBlueprints | GetBlueprintsByName_blueprintsByName
  ): PortalModuleBlueprint {
    return new PortalModuleBlueprint(
      gql.id,
      gql.name,
      gql.status,
      gql.xAttachmentPoints.map(PortalModuleBlueprint.xAPFromGql),
      gql.yAttachmentPoints.map(PortalModuleBlueprint.yAPFromGql),
      gql.model ? PortalAsset.fromGql(gql.model) : undefined,
      gql.invariants.map(PortalInvariant.fromGql),
      new Events(
        gql.events.onAttach,
        gql.events.onDelete,
        gql.events.onCreated,
        null
      ),
      gql.eventTemplate,
      gql.invariantTemplates,
      gql.visualXAttachmentPoints?.map(VisualXAttachmentPoint.fromRaw),
      gql.visualYAttachmentPoint ? VisualYAttachmentPoint.fromRaw(gql.visualYAttachmentPoint) : undefined,
      gql.groupId,
      gql.boundingBoxes.map(bbox =>
        new BoundingBox(
          vectorFromRaw(PortalVector3.fromGql(bbox.position)),
          new BoundingBoxSize(
            bbox.size.width,
            bbox.size.height,
            bbox.size.length
          ),
          BbColor.fromRaw({ color: bbox.color.color, id: bbox.color.id, overlapAllowed: bbox.color.overlapAllowed })
        )
      ),
      gql.categoryId,
      gql.categoryReference,
      gql.moduleType,
      gql.considerAsFree,
      gql.orderWeight
    );
  }

  toGqlInput(): ModuleBlueprintInput {
    return {
      events: this.events ?? {},
      categoryId: this.categoryId ?? _id('1'),
      categoryReference: this.categoryReference ?? '',
      invariants: this.invariants?.map(PortalModuleBlueprint.invariantToGqlInput) ?? [],
      moduleType: this.moduleType ?? ModuleType.Aux,
      name: this.name ?? 'NAME',
      xAttachmentPoints: this.xAttachmentPoints?.map(PortalModuleBlueprint.xAPToGqlInput) ?? [],
      yAttachmentPoints: this.yAttachmentPoints?.map(PortalModuleBlueprint.yAPToGqlInput) ?? [],
      invariantTemplates: this.invariantTemplates,
      model: (this.model ? (this.model as PortalAsset) : new PortalAsset(_id(''), '', '')).toGqlInput(),
      visualXAttachmentPoints: this.visualXAttachmentPoints ?? [],
      visualYAttachmentPoint: this.visualYAttachmentPoint,
      eventTemplateId: this.eventTemplate,
      status: this.status
        ? PortalModuleBlueprint.statusToGqlInput(this.status)
        : PortalModuleBlueprint.statusToGqlInput(draftStatus),
      groupId: this.groupId,
      boundingBoxes: this.boundingBoxes.map(bbox => ({
        color: PortalModuleBlueprint.colorToGqlInput(bbox.color),
        size: bbox.size,
        position: PortalVector3.fromThree(bbox.position).toGqlInput(),
      })),
      considerAsFree: this.considerAsFree,
      orderWeight: this.orderWeight,
    };
  }

  static toJsonInput(blueprintInput: ModuleBlueprintInput): JSON {
    return { blueprint: blueprintInput } as object as JSON;
  }

  private static statusToGqlInput(status: VersionedStatus): VersionedStatusInput {
    switch (status.__typename) {
      case 'Draft':
        return {
          draft: { dummy: '' },
          type: VersionedStatusTypes.Draft,
        };
      case 'Published':
        return {
          published: { dummy: '' },
          type: VersionedStatusTypes.Published,
        };
      case 'Archived':
        return {
          archived: { version: status.version },
          type: VersionedStatusTypes.Archived,
        };
    }
  }

  private static colorToGqlInput(color: BbColor): BbColorInput {
    switch (color.color) {
      case 'Red': return { color: ColorType.Red };
      case 'Orange': return { color: ColorType.Orange };
      case 'Yellow': return { color: ColorType.Yellow };
      case 'Purple': return { color: ColorType.Purple, id: id(color.id), overlapAllowed: color.overlapAllowed };
      default: return { color: ColorType.Red };
    }
  }

  private static xAPFromGql(gql: GetAllModuleBlueprints_allBlueprints_xAttachmentPoints): XAttachmentPoint {
    return new XAttachmentPoint(
      gql.id,
      vectorFromRaw(PortalVector3.fromGql(gql.position)),
      vectorFromRaw(PortalVector3.fromGql(gql.orientation)),
      new Set(gql.properties.map(propertyArray => new Set(propertyArray)))
    );
  }

  private static xAPToGqlInput(xap: XAttachmentPoint): XAttachmentPointInput {
    return {
      id: xap.id,
      orientation: PortalVector3.fromThree(xap.orientation).toGqlInput(),
      position: PortalVector3.fromThree(xap.position).toGqlInput(),
      properties: xap.properties ? Array.from(xap.properties).map(propertySet => Array.from(propertySet)) : [],
    };
  }

  private static yAPFromGql(gql: GetAllModuleBlueprints_allBlueprints_yAttachmentPoints): YAttachmentPoint {
    return new YAttachmentPoint(
      gql.id,
      vectorFromRaw(PortalVector3.fromGql(gql.position)),
      vectorFromRaw(PortalVector3.fromGql(gql.orientation)),
      new Set<string>(gql.properties)
    );
  }

  private static yAPToGqlInput(yap: YAttachmentPoint): YAttachmentPointInput {
    return {
      id: yap.id,
      orientation: PortalVector3.fromThree(yap.orientation).toGqlInput(),
      position: PortalVector3.fromThree(yap.position).toGqlInput(),
      properties: yap.properties ? Array.from(yap.properties) : [],
    };
  }

  static invariantToGqlInput(inv: PortalInvariant): InvariantInput {
    return {
      id: inv.id,
      translations: PortalModuleBlueprint.invariantTranslationsToGqlInput(inv.translations),
      name: inv.name,
      rule: inv.rule,
      correction: inv.correction,
      disableDuringOnChange: inv.disableDuringOnChange,
    };
  }

  private static invariantTranslationsToGqlInput(t: Map<string, string>): InvariantTranslationKeyStringInputMap {
    return {
      values: Array.from(t).map(([k, v]) => PortalModuleBlueprint.invariantTranslationToGqlInput(k, v)),
    };
  }

  private static stringToInvariantKey(sk: string): InvariantTranslationKey {
    switch (sk) {
      case 'Correction':
        return InvariantTranslationKey.Correction;
      case 'Error':
        return InvariantTranslationKey.Error;
    }
  }

  private static invariantTranslationToGqlInput(k: string, v: string): InvariantTranslationKeyStringInputPair {
    return {
      key: PortalModuleBlueprint.stringToInvariantKey(k),
      value: v,
    };
  }
}
